/**
 * Lobby: the research hall (world/Lobby.ts). Onboarding (name, role, avatar) happens at the
 * reception, where four stations acknowledge each step; then the study directory takes the
 * card's place. Studies are chosen from the directory (here or, larger, beside the Study
 * Gateway) and entered through the gateway, its Enter buttons or 1–9 on desktop. Both
 * directories share one query and one selection. The visitor's journey drives the hall's
 * light (pathways, pools, the sculpture and the gateway).
 *
 * Entering asks for the room code first (`RoomCodePanel`, 2026-10-06): a group shares a
 * three character code and meets in its own room of the study.
 */
import * as THREE from 'three';
import { ROLE_STYLES, type Role } from '@clinspace/shared';
import { BUILT_IN_AVATARS, isValidName, type AvatarId } from '../app/profile.ts';
import { TextField } from '../queries/textField.ts';
import { Keyboard } from '../ui/CanvasKeyboard.ts';
import { faceCamera } from '../ui/Panel.ts';
import { TOKENS, luminous } from '../ui/theme.ts';
import { setUiMode } from '../dashboards/charts.ts';
import { GATEWAY, LOBBY, RECEPTION, buildLobby, type LobbyWorld } from '../world/Lobby.ts';
import type { LoadedAvatar } from '../world/avatars.ts';
import type { StationView } from '../world/lobby/reception.ts';
import { DEFAULT_QUERY, type DirectoryQuery } from './lobby/directory.ts';
import { OnboardingPanel, type OnboardingStep } from './lobby/OnboardingPanel.ts';
import { RoomCodePanel } from './lobby/RoomCodePanel.ts';
import { warmSpeech } from '../assistant/hostVoice.ts';
import { welcomeLine } from '../assistant/tour.ts';
import { prefetchRoomData } from './RoomScene.ts';
import { assistantInfo } from '../assistant/info.ts';
import { StudyDetail, StudyDirectory, type DirectoryHost } from './lobby/StudyDirectory.ts';
import type { AppContext, SceneController, TeleportAnchor } from './Scene.ts';

type Step = OnboardingStep | 'done';

/** Where the gateway's directory and detail panels face: down the hall toward arrivals. */
const GATEWAY_FACING = new THREE.Vector3(0, 0, -3);

export class LobbyScene implements SceneController {
  readonly name = 'lobby';
  readonly group = new THREE.Group();
  readonly canLeave = false;
  readonly anchors: TeleportAnchor[] = [
    { label: 'Reception', position: RECEPTION.spawn.clone() },
    { label: 'Sculpture', position: new THREE.Vector3(3.2, 0, 5.4) },
    { label: 'Gateway', position: GATEWAY.approach.clone() },
    { label: 'Lounge', position: new THREE.Vector3(9.2, 0, 4.2) },
  ];
  private world: LobbyWorld;
  step: Step = 'name';
  private onboarding = new THREE.Group();
  private keyboard: Keyboard;
  private card: OnboardingPanel;
  /** The directory at the reception (after onboarding) and the one beside the gateway. */
  private directory: StudyDirectory;
  private gatewayDirectory: StudyDirectory;
  private detail: StudyDetail;
  private directoryGroup = new THREE.Group();
  private query: DirectoryQuery = { ...DEFAULT_QUERY };
  selected: string | undefined;
  private readonly nameField = new TextField();
  private readonly vrmField = new TextField();
  private focusedField: 'name' | 'vrm' = 'name';
  private nameError = '';
  private vrmStatus = '';
  private turntable = new THREE.Group();
  private previewAvatar: LoadedAvatar | undefined;
  private previewLoading: string | undefined;
  private unsubscribeRooms: (() => void) | undefined;
  private unsubscribeKeys: (() => void) | undefined;
  private entering = false;
  /** Asks for the room code before entering a study. */
  readonly roomCode: RoomCodePanel;
  /** Whether the visitor stood in the gateway last frame (walking in asks once). */
  private inGateway = false;
  private elapsed = 0;
  private savedBackground: THREE.Scene['background'] = null;
  private savedFog: THREE.Scene['fog'] = null;

  get previewLoaded(): boolean {
    return this.previewAvatar !== undefined && this.previewLoading === undefined;
  }

  /** Codes on the reception directory's current page (desktop keys 1–9). */
  get pageCodes(): string[] {
    return this.directory.pageCodes;
  }

  constructor(private readonly ctx: AppContext) {
    // The lobby is dark; set before any panel is built or drawn.
    setUiMode('dark');
    this.world = buildLobby(ctx.renderer);
    this.group.add(this.world.group);
    this.keyboard = new Keyboard(ctx.interactive, {
      onInput: (c) => this.typeChar(c),
      onBackspace: () => this.backspace(),
      onEnter: () => this.submitField(),
      onKeySound: () => ctx.audio.key(),
    });
    this.nameField.value = ctx.profile.name;
    this.vrmField.value = ctx.profile.vrmUrl ?? '';
    this.card = new OnboardingPanel(
      ctx.interactive,
      () => ({
        step: this.step === 'done' ? 'avatar' : this.step,
        name: this.nameField,
        vrm: this.vrmField,
        focused: this.focusedField,
        nameError: this.nameError,
        vrmStatus: this.vrmStatus,
        role: ctx.profile.role,
        avatar: ctx.profile.vrmUrl ? undefined : ctx.profile.avatar,
      }),
      {
        submitName: () => this.submitName(),
        chooseRole: (role) => this.chooseRole(role),
        chooseAvatar: (id) => this.chooseAvatar(id),
        loadVrm: () => void this.loadVrm(),
        finish: () => this.finishOnboarding(),
        back: () => this.showStep(this.step === 'avatar' ? 'role' : 'name'),
        next: () => {
          if (this.step === 'name') this.submitName();
          else if (this.step === 'role') this.showStep('avatar');
        },
        focus: (field) => this.focusField(field),
      },
      { click: () => ctx.audio.click() },
    );
    const host: DirectoryHost = {
      query: () => this.query,
      setQuery: (q) => {
        this.query = q;
        this.ctx.audio.click();
        this.refreshDirectories();
      },
      selected: () => this.selected,
      select: (code) => this.selectStudy(code),
      enter: (code) => this.enterRoom(code),
    };
    this.directory = new StudyDirectory(ctx.interactive, ctx.manifest, host, {
      width: 1.95,
      name: 'study-directory',
    });
    this.gatewayDirectory = new StudyDirectory(ctx.interactive, ctx.manifest, host, {
      width: 3.4,
      name: 'gateway-directory',
    });
    this.detail = new StudyDetail(ctx.interactive, ctx.manifest, host, 1.9);
    this.roomCode = new RoomCodePanel(
      ctx.interactive,
      {
        enter: (study, code) => this.goIn(study, code),
        cancel: () => this.ctx.audio.click(),
      },
      {
        key: () => ctx.audio.key(),
        click: () => ctx.audio.click(),
        typing: (on) => {
          ctx.input.captureText = on || this.step === 'name' || this.step === 'avatar';
        },
      },
    );
    this.group.add(this.roomCode.group);
    this.placeGatewayPanels();
    this.buildOnboarding();
  }

  /* ---------- Lifecycle ---------- */

  enter(): void {
    const { ctx } = this;
    this.savedBackground = ctx.scene.background;
    this.savedFog = ctx.scene.fog;
    ctx.scene.background = new THREE.Color(LOBBY.background);
    ctx.scene.fog = new THREE.Fog(LOBBY.fog.colour, LOBBY.fog.near, LOBBY.fog.far);
    ctx.locomotion.bounds = this.world.bounds;
    ctx.locomotion.floors = this.world.floors;
    ctx.rig.group.position.set(LOBBY.spawn.x, ctx.rig.heightOffset, LOBBY.spawn.z);
    ctx.rig.faceTowards(LOBBY.lookAt, ctx.inXr());
    ctx.audio.setHum(true);
    this.entering = false;
    this.unsubscribeKeys = ctx.input.onKey((e) => this.onKey(e));
    this.unsubscribeRooms = ctx.net.onRooms((rooms) => this.setRooms(rooms));
    if (
      ctx.profile.onboarded &&
      isValidName(ctx.profile.name) &&
      ctx.net.sessionId !== undefined &&
      ctx.net.currentRoom
    ) {
      // Returning from a room: skip onboarding.
      this.showStep('done');
      ctx.net.joinLobby();
    } else {
      this.showStep('name');
    }
  }

  exit(): void {
    this.ctx.input.captureText = false;
    this.unsubscribeKeys?.();
    this.unsubscribeRooms?.();
    this.ctx.audio.setHum(false);
    this.ctx.scene.background = this.savedBackground;
    this.ctx.scene.fog = this.savedFog;
    this.card.dispose(this.ctx.interactive);
    this.roomCode.dispose();
    this.directory.dispose(this.ctx.interactive);
    this.gatewayDirectory.dispose(this.ctx.interactive);
    this.detail.dispose(this.ctx.interactive);
    this.world.dispose();
  }

  update(dt: number): void {
    this.elapsed += dt;
    this.world.update(dt);
    this.keyboard.update();
    this.card.update();
    this.directory.update();
    this.gatewayDirectory.update();
    this.detail.update();
    this.roomCode.update();
    if (this.previewAvatar?.mixer) this.previewAvatar.mixer.update(dt);
    this.turntable.rotation.y += dt * 0.35;
    if (this.step !== 'done') faceCamera(this.onboarding, this.ctx.rig.camera);
    else faceCamera(this.directoryGroup, this.ctx.rig.camera);
    const feet = this.ctx.rig.feetPosition(new THREE.Vector3());
    const d = Math.hypot(feet.x - RECEPTION.spawn.x, feet.z - RECEPTION.spawn.z);
    this.world.setJourney({
      step: this.step,
      selected: this.selectedStudy(),
      nearReception: THREE.MathUtils.clamp(1 - (d - 2) / 6, 0, 1),
    });
    if (this.step === 'done' && !this.entering) this.checkGateway(feet);
  }

  /* ---------- Rooms and the directory ---------- */

  private setRooms(rooms: AppContext['rooms']): void {
    this.directory.setRooms(rooms);
    this.gatewayDirectory.setRooms(rooms);
    this.detail.setRooms(rooms);
  }

  private refreshDirectories(): void {
    this.directory.invalidate();
    this.gatewayDirectory.invalidate();
    this.detail.invalidate();
    this.refreshStations();
  }

  private selectedStudy(): { code: string; title: string; colour: string } | undefined {
    const s = this.ctx.manifest.studies.find((x) => x.code === this.selected);
    return s ? { code: s.code, title: s.shortTitle, colour: luminous(s.accentColour) } : undefined;
  }

  selectStudy(code: string): void {
    this.selected = this.selected === code ? undefined : code;
    this.ctx.audio.click();
    this.refreshDirectories();
  }

  private placeGatewayPanels(): void {
    const place = (mesh: THREE.Object3D, p: { x: number; y: number; z: number }): void => {
      mesh.position.set(p.x, p.y, p.z);
      mesh.rotation.y = Math.atan2(GATEWAY_FACING.x - p.x, GATEWAY_FACING.z - p.z);
      this.group.add(mesh);
    };
    place(this.gatewayDirectory.mesh, GATEWAY.directory);
    place(this.detail.mesh, GATEWAY.detail);
  }

  /* ---------- Onboarding ---------- */

  private buildOnboarding(): void {
    this.onboarding.position.copy(LOBBY.onboardingPosition);
    this.group.add(this.onboarding);
    this.card.mesh.position.set(0, 0.08, 0);
    this.onboarding.add(this.card.mesh);
    this.keyboard.group.position.set(0, -0.6, 0.25);
    this.keyboard.group.rotation.x = -0.45;
    this.onboarding.add(this.keyboard.group);

    // The study directory takes the card's place at the reception after onboarding.
    this.directoryGroup.position.copy(LOBBY.onboardingPosition);
    this.directoryGroup.position.y += 0.2;
    this.directoryGroup.add(this.directory.mesh);
    this.group.add(this.directoryGroup);

    // Turntable beside the card: a dark disc with a ring of light.
    this.turntable.position.set(1.25, -1.45, -0.4);
    const disc = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.55, 0.06, 48),
      new THREE.MeshStandardMaterial({ color: '#10151c', roughness: 0.2, metalness: 0.7 }),
    );
    disc.position.y = 0.03;
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.52, 0.012, 6, 64),
      new THREE.MeshBasicMaterial({ color: TOKENS.cyan, toneMapped: false }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.065;
    this.turntable.add(disc, ring);
    this.onboarding.add(this.turntable);
  }

  private showStep(step: Step): void {
    this.step = step;
    this.turntable.visible = step === 'avatar';
    this.keyboard.setVisible(step === 'name' || step === 'avatar');
    this.ctx.input.captureText = step === 'name' || step === 'avatar';
    this.onboarding.visible = step !== 'done';
    this.directoryGroup.visible = step === 'done';
    // The gateway's panels only act once the visitor has a name and role.
    this.gatewayDirectory.mesh.visible = step === 'done';
    this.detail.mesh.visible = step === 'done';
    if (step === 'name') this.focusField('name');
    if (step === 'avatar') {
      this.focusField('vrm');
      void this.showPreview(this.ctx.profile.avatar);
    }
    this.card.invalidate();
    this.refreshStations();
    if (step === 'done') {
      this.setRooms(this.ctx.rooms);
      this.ctx.net.identify(this.ctx.profile);
      this.ctx.net.joinLobby();
      this.ctx.input.centreRay = !this.ctx.inXr();
      this.ctx.input.lockOnClick = !this.ctx.inXr();
      if (!this.ctx.inXr()) this.ctx.input.requestLock();
    } else {
      this.ctx.input.centreRay = false;
      this.ctx.input.lockOnClick = false;
      if (document.pointerLockElement) document.exitPointerLock();
    }
  }

  /** The reception's stations: what is done, what is now, what comes next. */
  private refreshStations(): void {
    const order: Step[] = ['name', 'role', 'avatar', 'done'];
    const at = order.indexOf(this.step);
    const state = (i: number): StationView['state'] =>
      i < at ? 'done' : i === at ? 'active' : 'pending';
    const p = this.ctx.profile;
    const avatar = p.vrmUrl
      ? 'Own VRM avatar'
      : BUILT_IN_AVATARS.find((a) => a.id === p.avatar)?.name;
    const study = this.selectedStudy();
    const views: StationView[] = [
      { number: '01', label: 'Identity', state: state(0), value: at > 0 ? p.name : undefined },
      {
        number: '02',
        label: 'Role',
        state: state(1),
        value: at > 1 ? p.role : undefined,
        colour: at > 1 ? ROLE_STYLES[p.role].hex : undefined,
      },
      { number: '03', label: 'Presence', state: state(2), value: at > 2 ? avatar : undefined },
      {
        number: '04',
        label: 'Study',
        state: study ? 'done' : state(3),
        value: study ? `${study.code}` : at === 3 ? 'Choose from the directory' : undefined,
        colour: study?.colour,
      },
    ];
    this.world.setStations(views);
  }

  private focusField(which: 'name' | 'vrm'): void {
    this.focusedField = which;
    this.card.invalidate();
  }

  private typeChar(c: string): void {
    if (this.focusedField === 'name' && this.step === 'name') {
      this.nameField.value = (this.nameField.value + c).slice(0, 24);
      this.nameError = '';
    } else if (this.focusedField === 'vrm' && this.step === 'avatar') {
      this.vrmField.value = (this.vrmField.value + c).slice(0, 512);
      this.vrmStatus = '';
    }
    this.card.invalidate();
  }

  private backspace(): void {
    const field = this.focusedField === 'name' ? this.nameField : this.vrmField;
    field.backspace();
    this.card.invalidate();
  }

  private submitField(): void {
    if (this.step === 'name') this.submitName();
    else if (this.step === 'avatar') void this.loadVrm();
  }

  private submitName(): void {
    const name = this.nameField.trimmed;
    if (!isValidName(name)) {
      this.nameError = 'Please use 2 to 24 characters.';
      this.card.invalidate();
      return;
    }
    this.ctx.profile.name = name;
    this.ctx.saveProfile();
    this.ctx.audio.click();
    this.showStep('role');
  }

  chooseRole(role: Role): void {
    this.ctx.profile.role = role;
    this.ctx.saveProfile();
    this.ctx.audio.click();
    this.showStep('avatar');
  }

  chooseAvatar(id: AvatarId): void {
    this.ctx.profile.avatar = id;
    delete this.ctx.profile.vrmUrl;
    this.vrmField.clear();
    this.vrmStatus = '';
    this.ctx.saveProfile();
    this.ctx.audio.click();
    this.card.invalidate();
    void this.showPreview(id);
  }

  private async showPreview(id: AvatarId): Promise<void> {
    this.previewLoading = id;
    try {
      const avatar = await this.ctx.avatars.loadBuiltIn(id, { animation: 'idle' });
      if (this.previewLoading !== id) return;
      this.setPreview(avatar);
      this.previewLoading = undefined;
    } catch (err) {
      console.warn('[clinspace] avatar preview failed', err);
    }
  }

  private setPreview(avatar: LoadedAvatar): void {
    if (this.previewAvatar) {
      this.turntable.remove(this.previewAvatar.root);
      this.previewAvatar.mixer?.stopAllAction();
    }
    avatar.root.position.y = 0.06;
    this.turntable.add(avatar.root);
    this.previewAvatar = avatar;
  }

  private async loadVrm(): Promise<void> {
    const url = this.vrmField.trimmed;
    if (!/^https?:\/\/.+\.vrm(\?.*)?$/i.test(url)) {
      this.vrmStatus = 'Enter a link ending in .vrm (http or https).';
      this.card.invalidate();
      return;
    }
    this.previewLoading = url;
    this.vrmStatus = '';
    try {
      const avatar = await this.ctx.avatars.loadVrm(url);
      if (this.previewLoading !== url) return;
      this.setPreview(avatar);
      this.previewLoading = undefined;
      this.ctx.profile.vrmUrl = url;
      this.ctx.saveProfile();
      this.ctx.audio.chime();
    } catch (err) {
      console.warn('[clinspace] VRM load failed', err);
      this.vrmStatus = 'Could not load that VRM. Check the link and try again.';
    }
    this.card.invalidate();
  }

  finishOnboarding(): void {
    this.ctx.profile.onboarded = true;
    this.ctx.saveProfile();
    this.ctx.audio.chime();
    this.showStep('done');
  }

  private onKey(e: KeyboardEvent): void {
    if (this.roomCode.isOpen) {
      if (this.roomCode.handleKeyEvent(e)) e.preventDefault();
      return;
    }
    if (this.step === 'name' || this.step === 'avatar') {
      if (this.keyboard.handleKeyEvent(e)) e.preventDefault();
    } else if (this.step === 'done' && /^Digit[1-9]$/.test(e.code)) {
      // 1–9 enter the studies on the directory's current page.
      const code = this.directory.pageCodes[Number(e.code.slice(5)) - 1];
      if (code) this.enterRoom(code);
    }
  }

  /* ---------- The gateway ---------- */

  private checkGateway(feet: THREE.Vector3): void {
    const z = GATEWAY.zone;
    const inside = feet.x > z.minX && feet.x < z.maxX && feet.z > z.minZ && feet.z < z.maxZ;
    // Walking in asks for the code once; stepping out and back in asks again.
    if (inside && !this.inGateway && this.selected) this.enterRoom(this.selected);
    this.inGateway = inside;
  }

  /** Entering a study: first the room code. */
  enterRoom(code: string): void {
    if (this.entering) return;
    const s = this.ctx.manifest.studies.find((x) => x.code === code);
    if (!s) return;
    this.selected = code;
    this.refreshDirectories();
    this.ctx.audio.click();
    // While the code is typed: Luma's body and the start of her welcome, so she greets the
    // visitor as the room appears rather than seconds later (2026-10-06).
    this.ctx.avatars.warmHost();
    prefetchRoomData(s.code);
    assistantInfo().catch(() => undefined);
    warmSpeech(welcomeLine({ code: s.code, visitor: this.ctx.profile.name }));
    this.roomCode.open(
      { code: s.code, title: s.shortTitle, colour: luminous(s.accentColour) },
      this.ctx.rig.headPosition(new THREE.Vector3()),
      this.ctx.rig.headForward(new THREE.Vector3()),
    );
  }

  /** Through the gateway into the study's room with this code. */
  private goIn(study: string, roomCode: string): void {
    if (this.entering) return;
    this.entering = true;
    this.world.gateway.flash();
    this.ctx.audio.doorSlide();
    this.ctx.audio.chime();
    setTimeout(() => this.ctx.enterRoom(study, roomCode), 450);
  }
}

/** Kept for the glyph test and older references: the built-in avatar ids in order. */
export const LOBBY_AVATAR_IDS = BUILT_IN_AVATARS.map((a) => a.id);
